import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, bigint, boolean, timestamp, jsonb, numeric, unique, uniqueIndex, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { workers } from './workers';
import { participationRoleTypes } from './partner';

declare const __tenantIdBrand: unique symbol;
export type TenantId = string & { readonly [__tenantIdBrand]: true };

export function asTenantId(id: string): TenantId {
  return id as TenantId;
}

export function isTenantId(value: unknown): value is TenantId {
  return typeof value === "string" && value.length > 0;
}

// ── Pack 12.C — Tenant theme preferences ────────────────────────────────────
// One value per tenant; applied for every tenant user via ThemeContext on the
// client (CSS variables) and read on every tenant-config endpoint hit.

export const TENANT_THEME_DENSITIES = ["compact", "comfortable", "spacious"] as const;
export type TenantThemeDensity = (typeof TENANT_THEME_DENSITIES)[number];

export const TENANT_THEME_FONT_SCALES = ["default", "large"] as const;
export type TenantThemeFontScale = (typeof TENANT_THEME_FONT_SCALES)[number];

export const TENANT_THEME_COLOR_MODES = ["light", "dark", "auto"] as const;
export type TenantThemeColorMode = (typeof TENANT_THEME_COLOR_MODES)[number];

const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

export const tenantThemePrefsSchema = z.object({
  primaryColor: z.string().regex(HEX_COLOR_RE).nullable().optional(),
  accentColor: z.string().regex(HEX_COLOR_RE).nullable().optional(),
  density: z.enum(TENANT_THEME_DENSITIES).optional(),
  fontScale: z.enum(TENANT_THEME_FONT_SCALES).optional(),
  colorMode: z.enum(TENANT_THEME_COLOR_MODES).optional(),
});
export type TenantThemePrefs = z.infer<typeof tenantThemePrefsSchema>;

export const DEFAULT_TENANT_THEME_PREFS: Required<{
  primaryColor: string | null;
  accentColor: string | null;
  density: TenantThemeDensity;
  fontScale: TenantThemeFontScale;
  colorMode: TenantThemeColorMode;
}> = {
  primaryColor: null,
  accentColor: null,
  density: "comfortable",
  fontScale: "default",
  colorMode: "auto",
};

export function resolveTenantThemePrefs(prefs: TenantThemePrefs | null | undefined) {
  return { ...DEFAULT_TENANT_THEME_PREFS, ...(prefs ?? {}) };
}

// ── Pack 13.M — Data residency regions (regulatory pin) ─────────────────────
// Supported regions for the per-tenant residency declaration. See
// docs/source-of-truth/compliance/data-residency-matrix.md §1 for the
// canonical authority on which jurisdictions each region covers.
export const DATA_RESIDENCY_REGIONS = ["ksa", "uae", "uk", "eu", "global"] as const;
export type DataResidencyRegion = (typeof DATA_RESIDENCY_REGIONS)[number];
export const dataResidencyRegionSchema = z.enum(DATA_RESIDENCY_REGIONS);

export const tenants = pgTable("tenants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: text("slug").notNull().unique(),
  status: text("status").notNull().default("active"),
  primaryRegionId: text("primary_region_id").references(() => regions.id),
  subscriptionTier: text("subscription_tier").notNull().default("free"),
  customDomain: text("custom_domain"),
  logoUrl: text("logo_url"),
  logoUrlDark: text("logo_url_dark"),
  primaryColor: text("primary_color"),
  tagline: text("tagline"),
  website: text("website"),
  address: text("address"),
  systemHostCompanyId: text("system_host_company_id"),
  industry: text("industry"),
  /**
   * Pack 11.A — WorkOS Enterprise Authentication Platform (Task #1115).
   *
   * `workosOrgId` binds this tenant to its WorkOS Organisation. Set when a
   * Super Admin onboards an enterprise tenant onto SSO; null for tenants
   * that authenticate via the legacy email/password path.
   *
   * `workosDirectoryId` is the WorkOS Directory id (the tenant's IdP
   * directory connection that pushes SCIM events). Set the first time a
   * `dsync.activated` webhook arrives for the tenant's organisation.
   */
  workosOrgId: text("workos_org_id").unique("uq_tenants_workos_org_id"),
  workosDirectoryId: text("workos_directory_id").unique("uq_tenants_workos_directory_id"),
  /**
   * Pack 11.A — WorkOS Consolidation, Hardening & Super-Admin-Only
   * Management (Task #1539).
   *
   * Per-tenant SSO opt-in. Default `false` so SSO is strictly opt-in: a
   * tenant only sees the "Continue with SSO" button (and only the SSO
   * endpoints will accept their email domain) once a Super Admin or
   * Tenant Admin holding `settings.manage` has flipped the toggle on.
   *
   * Backfilled to `true` for tenants that already had an `active`
   * `sso_connections` row at migration time, so currently-working
   * enterprise sign-ins survive the cut-over without manual action.
   */
  ssoEnabled: boolean("sso_enabled").notNull().default(false),
  /**
   * Per-tenant Apprise SMS provider URL override (Pack 4 / G-007).
   *
   * When set, sms-service prefers this value over the platform-wide
   * `SMS_PROVIDER_URL` env-var or the `sms_provider_url` row in
   * `platform_settings`. This lets regulated / enterprise tenants route
   * SMS through their own vendor account so billing + delivery analytics
   * stay on their side. Format mirrors the global setting:
   *   twilio://AccountSID:AuthToken@FromNumber
   *   vonage://ApiKey:ApiSecret@FromNumber
   * If null/empty the global default applies.
   */
  smsProviderUrl: text("sms_provider_url"),
  /**
   * Per-tenant theme preferences (Pack 12.C / Task #1035).
   * Shape (mirrored by `tenantThemePrefsSchema` below):
   *   { primaryColor, accentColor, density, fontScale, colorMode }
   * Stored as a single jsonb so future keys can be added without ALTER TABLE.
   * Engine defaults live in code (see `DEFAULT_TENANT_THEME_PREFS`).
   */
  themePrefs: jsonb("theme_prefs").$type<TenantThemePrefs>().notNull().default(sql`'{}'::jsonb`),
  /**
   * Pack 13.M — per-tenant data residency declaration.
   *
   * `dataResidencyRegion` is the regulatory pin for this tenant — one of
   * `ksa | uae | uk | eu | global` (engine treats `null` as `global`).
   * `residencyLocked` (when true) refuses any change to this column or to
   * `primaryRegionId` outside of the dedicated unlock workflow on
   * `PATCH /api/super-admin/tenants/:id/residency`.
   *
   * Lock metadata (`residencyLockedAt`, `residencyLockedBy`,
   * `residencyLockReason`) is set by the lock action and cleared by the
   * unlock action; both emit canonical audit events.
   *
   * Source-of-truth: docs/source-of-truth/compliance/data-residency-matrix.md.
   */
  dataResidencyRegion: text("data_residency_region"),
  residencyLocked: boolean("residency_locked").notNull().default(false),
  residencyLockedAt: timestamp("residency_locked_at", { withTimezone: true }),
  residencyLockedBy: text("residency_locked_by"),
  residencyLockReason: text("residency_lock_reason"),
  seedConfig: jsonb("seed_config").$type<Record<string, any>>(),
  /**
   * LEGACY READ-ONLY: tenants.settings.features is a legacy feature flag store.
   * New code must NOT write feature flags here. Use the tenant_features table exclusively.
   * At login, settings.features is merged with tenant_features for backward compatibility.
   * This field is retained only to avoid breaking existing tenants with settings written before
   * the tenant_features table existed. See server/routes.ts login merge logic for merge order.
   */
  settings: jsonb("settings").$type<Record<string, any>>().default({}),
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxTenantsIndustry: index("idx_tenants_industry").on(t.industry),
  // Pack 13.M — partial index on residency region for the SA "tenants currently
  // pinned to <region>" query path. The partial-WHERE (residency_locked = true)
  // is created at startup by `server/schema-drift-fix.ts` (CREATE INDEX
  // CONCURRENTLY cannot run inside Drizzle's per-migration transaction, and a
  // non-CONCURRENT CREATE INDEX in migration 0099 would trip the
  // [concurrent-index-missing] migration-safety rule). Drizzle doesn't model
  // the WHERE clause; declaring the index here keeps validate-schema CHECK 1
  // GREEN once the drift-fix has run.
  idxTenantsResidencyLockedRegion: index("idx_tenants_residency_locked_region").on(t.dataResidencyRegion),
}));
export const insertTenantSchema = createInsertSchema(tenants).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTenant = z.infer<typeof insertTenantSchema>;
export type Tenant = typeof tenants.$inferSelect;
export const tenantMemberships = pgTable("tenant_memberships", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: text("user_id").notNull().references(() => users.id),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  role: text("role").notNull().default("Viewer"),
  isPrimary: boolean("is_primary").notNull().default(false),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxTenantMembershipsUserTenant: index("idx_tenant_memberships_user_tenant").on(t.userId, t.tenantId),
  idxTenantMembershipsTenantRole: index("idx_tenant_memberships_tenant_role").on(t.tenantId, t.role),
  // Pack 5 (Task #924): backport of legacy migration 0075_sa_perf_indexes idx_tenant_memberships_tenant_status
  idxTenantMembershipsTenantStatus: index("idx_tenant_memberships_tenant_status").on(t.tenantId, t.status),
}));
export const insertTenantMembershipSchema = createInsertSchema(tenantMemberships).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTenantMembership = z.infer<typeof insertTenantMembershipSchema>;
export type TenantMembership = typeof tenantMemberships.$inferSelect;
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
  email: text("email").unique(),
  displayName: text("display_name"),
  role: text("role"),
  hasCompletedOnboarding: boolean("has_completed_onboarding").default(true),
  preferredLanguage: text("preferred_language"),
  phone: text("phone"),
  photoUrl: text("photo_url"),
  mfaEnabled: boolean("mfa_enabled").notNull().default(false),
  mfaSecret: text("mfa_secret"),
  mfaBackupCodes: text("mfa_backup_codes").array().default([]),
  mfaFailCount: integer("mfa_fail_count").notNull().default(0),
  mfaLockedUntil: timestamp("mfa_locked_until"),
  requirePasswordChange: boolean("require_password_change").notNull().default(false),
  // Pack 10.E (Task #1025) — Email deliverability hardening.
  // `email_status` is the suppression marker for hard bounces / complaints
  // produced by the provider webhook (`server/routes/webhooks/email.ts`).
  // Allowed values: "active" (default), "bounced", "complained",
  // "suppressed" (manual / global). When status != "active" the
  // email-service refuses to send to this user's address.
  emailStatus: text("email_status").default("active"),
  emailStatusReason: text("email_status_reason"),
  emailStatusUpdatedAt: timestamp("email_status_updated_at", { withTimezone: true }),
  /**
   * Pack 11.A — WorkOS Enterprise Authentication Platform (Task #1115).
   *
   * `workosUserId` is the canonical WorkOS user id. Set the first time a
   * user signs in via SSO (we look the user up by email and link the
   * record). Null for users who only authenticate via the legacy
   * email/password path.
   *
   * `workosDirectoryUserId` is the WorkOS Directory User id when the user
   * was provisioned via SCIM (separate from `workosUserId` — a user may
   * exist in WorkOS Auth but not in the Directory if they were created
   * before SCIM was activated).
   */
  workosUserId: text("workos_user_id").unique("uq_users_workos_user_id"),
  workosDirectoryUserId: text("workos_directory_user_id").unique("uq_users_workos_directory_user_id"),
  // Pack 9.E (#737, #738): optimistic concurrency for safe-edit. PATCH callers
  // must echo the current `version` (or send `If-Match: "<version>"`); a stale
  // write returns 409 VERSION_MISMATCH with the current row in `details`.
  version: integer("version").notNull().default(1),
  /**
   * GAP-064 (Task #3516) — temporary password expiry enforcement.
   * Set to NOW() + 24h whenever a Super Admin issues or resets a
   * temporary password. At login, if this column is set and in the past,
   * the credential is invalidated and `TEMPORARY_PASSWORD_EXPIRED` is
   * returned. If set and in the future, `PASSWORD_CHANGE_REQUIRED` is
   * returned without a full session so the user is forced to a change
   * screen before gaining access.
   */
  temporaryPasswordExpiresAt: timestamp("temporary_password_expires_at", { withTimezone: true }),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertUserSchema = createInsertSchema(users).pick({
  username: true,
  password: true,
  email: true,
  displayName: true,
  role: true,
  hasCompletedOnboarding: true,
  requirePasswordChange: true,
  temporaryPasswordExpiresAt: true,
});
export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;
export const invitations = pgTable("invitations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  token: text("token").notNull().unique(),
  email: text("email").notNull(),
  name: text("name").notNull(),
  role: text("role").notNull(),
  status: text("status").notNull().default("pending"),
  invitedBy: varchar("invited_by"),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxInvitationsTenantStatus: index("idx_invitations_tenant_status").on(t.tenantId, t.status),
}));
export const insertInvitationSchema = createInsertSchema(invitations).omit({
  id: true,
  createdAt: true,
 tenantId: true });
export type InsertInvitation = z.infer<typeof insertInvitationSchema>;
export type Invitation = typeof invitations.$inferSelect;

// Note: workers retains display-name text fields (company, area) for UI display
// alongside FK fields (companyId, primaryAreaId) for relational integrity.
// The text fields are populated by seed/UI, the FK fields are backfilled by migration.
export const CANONICAL_COMPANY_TYPES = [
  "tenant",
  "client",
  "owner",
  "operator",
  "contractor",
  "subcontractor",
  "joint_venture",
  "consultant",
  "vendor",
  "supplier",
  "government_authority",
  "labour_provider",
  "service_provider",
] as const;
export type CompanyType = typeof CANONICAL_COMPANY_TYPES[number];
export const ORGANIZATION_TYPES = [
  "tenant_internal",
  "system_host",
  "partner_legal",
  "legal_entity",
] as const;
export type OrganizationType = typeof ORGANIZATION_TYPES[number];
export const companies = pgTable("companies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  code: text("code"),
  legalName: text("legal_name"),
  displayName: text("display_name"),
  registrationNumber: text("registration_number"),
  licenseNumber: text("license_number"),
  taxNumber: text("tax_number"),
  companyType: text("company_type").notNull().default("contractor"),
  tenantId: text("tenant_id"),
  isPlatformEntity: boolean("is_platform_entity").notNull().default(false),
  organizationType: text("organization_type").notNull().default("tenant_internal"),
  ecosystemScope: text("ecosystem_scope"),
  contractId: text("contract_id"),
  activeWorkers: integer("active_workers").notNull().default(0),
  complianceScore: integer("compliance_score").notNull().default(100),
  status: text("status").notNull().default("active"),
  linkedSites: text("linked_sites").array().default([]),
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),
  contactAddress: text("contact_address"),
  contactCity: text("contact_city"),
  contactCountry: text("contact_country"),
  contactPostalCode: text("contact_postal_code"),
  website: text("website"),
  logoUrl: text("logo_url"),
  approvalStatus: text("approval_status").notNull().default("pending"),
  onboardingStatus: text("onboarding_status").notNull().default("not_started"),
  complianceStatus: text("compliance_status").notNull().default("pending"),
  insuranceStatus: text("insurance_status").notNull().default("unknown"),
  insuranceExpiryDate: text("insurance_expiry_date"),
  isBlacklisted: boolean("is_blacklisted").notNull().default(false),
  blacklistReason: text("blacklist_reason"),
  maySponsorWorkers: boolean("may_sponsor_workers").notNull().default(false),
  maxWorkersAllowed: integer("max_workers_allowed"),
  defaultCurrency: text("default_currency"),
  defaultAccessProfileId: text("default_access_profile_id"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
  isDraft: boolean("is_draft").notNull().default(false),
  draftSource: text("draft_source"),
  // Pack 9.E (#737, #738): optimistic concurrency for safe-edit. See note on `users.version`.
  version: integer("version").notNull().default(1),
}, (t) => ({
  idxCompaniesTenantCompanyType: index("idx_companies_tenant_company_type").on(t.tenantId, t.companyType),
  idxCompaniesTenantStatus: index("idx_companies_tenant_status").on(t.tenantId, t.status),
}));
export const insertCompanySchema = createInsertSchema(companies).omit({ tenantId: true, linkedSites: true, version: true }).extend({
  companyType: z.enum(CANONICAL_COMPANY_TYPES).default("contractor"),
  organizationType: z.enum(ORGANIZATION_TYPES).default("tenant_internal"),
});
export type InsertCompany = z.infer<typeof insertCompanySchema>;
export type Company = typeof companies.$inferSelect;
export const companyRelationships = pgTable("company_relationships", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  parentCompanyId: text("parent_company_id").notNull().references(() => companies.id),
  childCompanyId: text("child_company_id").notNull().references(() => companies.id),
  relationshipType: text("relationship_type").notNull().default("contractor_subcontractor"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  approvalStatus: text("approval_status").notNull().default("pending"),
  approvedBy: text("approved_by"),
  approvedDate: text("approved_date"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertCompanyRelationshipSchema = createInsertSchema(companyRelationships).omit({ tenantId: true });
export type InsertCompanyRelationship = z.infer<typeof insertCompanyRelationshipSchema>;
export type CompanyRelationship = typeof companyRelationships.$inferSelect;
export const companyWorkerLinks = pgTable("company_worker_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  companyId: text("company_id").notNull().references(() => companies.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  relationshipType: text("relationship_type").notNull().default("employer"),
  employmentCategory: text("employment_category"),
  isPrimary: boolean("is_primary").notNull().default(false),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  areaScope: text("area_scope"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  status: text("status").notNull().default("active"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertCompanyWorkerLinkSchema = createInsertSchema(companyWorkerLinks).omit({ tenantId: true });
export type InsertCompanyWorkerLink = z.infer<typeof insertCompanyWorkerLinkSchema>;
export type CompanyWorkerLink = typeof companyWorkerLinks.$inferSelect;
export const companyUserLinks = pgTable("company_user_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: text("company_id").notNull().references(() => companies.id),
  userId: text("user_id").notNull().references(() => users.id),
  jobTitle: text("job_title"),
  department: text("department"),
  departmentId: text("department_id"),
  functionRole: text("function_role"),
  isPrimary: boolean("is_primary").notNull().default(false),
  scopeType: text("scope_type"),
  scopeId: text("scope_id"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  invitationStatus: text("invitation_status").notNull().default("pending"),
  status: text("status").notNull().default("active"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  tenantId: text("tenant_id").references(() => tenants.id),
}, (t) => ({
  idxTenantCompany: index("idx_company_user_links_tenant_company").on(t.tenantId, t.companyId),
  idxCompanyUserLinksTenantUserId: index("idx_company_user_links_tenant_user_id").on(t.tenantId, t.userId),
}));
export const insertCompanyUserLinkSchema = createInsertSchema(companyUserLinks).omit({ id: true });
export type InsertCompanyUserLink = z.infer<typeof insertCompanyUserLinkSchema>;
export type CompanyUserLink = typeof companyUserLinks.$inferSelect;
export const companyRoleAssignments = pgTable("company_role_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  companyId: text("company_id").notNull().references(() => companies.id),
  userId: text("user_id").notNull().references(() => users.id),
  roleKey: text("role_key").notNull(),
  permissionBundle: text("permission_bundle").array().default([]),
  scopeType: text("scope_type").notNull().default("company"),
  scopeId: text("scope_id"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  status: text("status").notNull().default("active"),
  assignedBy: text("assigned_by"),
  notes: text("notes"),
  overrideAuditOnly: boolean("override_audit_only").default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertCompanyRoleAssignmentSchema = createInsertSchema(companyRoleAssignments).omit({ tenantId: true });
export type InsertCompanyRoleAssignment = z.infer<typeof insertCompanyRoleAssignmentSchema>;
export type CompanyRoleAssignment = typeof companyRoleAssignments.$inferSelect;
export const userAreaScopes = pgTable("user_area_scopes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  userId: text("user_id").notNull().references(() => users.id),
  areaId: text("area_id").notNull().references(() => areas.id),
  scopeType: text("scope_type").notNull().default("operational"),
  companyId: text("company_id"),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  status: text("status").notNull().default("active"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertUserAreaScopeSchema = createInsertSchema(userAreaScopes).omit({ tenantId: true });
export type InsertUserAreaScope = z.infer<typeof insertUserAreaScopeSchema>;
export type UserAreaScope = typeof userAreaScopes.$inferSelect;
export const companyAuditLog = pgTable("company_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  companyId: text("company_id").notNull(),
  entityType: text("entity_type").notNull(),
  entityId: text("entity_id"),
  actionType: text("action_type").notNull(),
  actorId: text("actor_id"),
  actorName: text("actor_name"),
  timestamp: text("timestamp").notNull(),
  previousValues: jsonb("previous_values").$type<Record<string, any>>(),
  newValues: jsonb("new_values").$type<Record<string, any>>(),
  changedFields: text("changed_fields").array().default([]),
  notes: text("notes"),
  ipAddress: text("ip_address"),
}, (t) => ({
  idxCompanyAuditLogTenantCompanyId: index("idx_company_audit_log_tenant_company_id").on(t.tenantId, t.companyId),
  idxCompanyAuditLogTenantTimestamp: index("idx_company_audit_log_tenant_timestamp").on(t.tenantId, t.timestamp),
}));
export const insertCompanyAuditLogSchema = createInsertSchema(companyAuditLog).omit({ tenantId: true });
export type InsertCompanyAuditLog = z.infer<typeof insertCompanyAuditLogSchema>;
export type CompanyAuditLogEntry = typeof companyAuditLog.$inferSelect;
export const policies = pgTable("policies", {
  id: varchar("id").primaryKey(),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  version: text("version").notNull(),
  scope: text("scope").notNull(),
  status: text("status").notNull().default("draft"),
  effectiveDate: text("effective_date"),
  linkedAreas: text("linked_areas").array().default([]),
  requiredCredentials: text("required_credentials").array().default([]),
  requiredApprovals: text("required_approvals").array().default([]),
  ptwRequiredWorkTypes: text("ptw_required_work_types").array().default([]),
  riskThreshold: text("risk_threshold").notNull().default("medium"),
  evidenceValidityMonths: integer("evidence_validity_months").notNull().default(12),
  renewalRequired: boolean("renewal_required").notNull().default(true),
  createdBy: text("created_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  applicableSites: text("applicable_sites").array().default([]),
});
export const insertPolicySchema = createInsertSchema(policies).omit({ tenantId: true });
export type InsertPolicy = z.infer<typeof insertPolicySchema>;
export type Policy = typeof policies.$inferSelect;

// ─── Claim Field Schema — Extended Types & Metadata ──────────────────
export const roles = pgTable("roles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull().unique(),
  description: text("description").notNull().default(""),
  isSystem: boolean("is_system").notNull().default(false),
  permissions: text("permissions").array().default([]),
  ptwSystemRoles: text("ptw_system_roles").array().default([]),
  tenantGroup: text("tenant_group"),
  color: text("color").notNull().default("blue"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertRoleSchema = createInsertSchema(roles).omit({ tenantId: true });
export type InsertRole = z.infer<typeof insertRoleSchema>;
export type Role = typeof roles.$inferSelect;

// ─── PTW Workflow Engine (v2) ────────────────────────────────────────

// ── PTW Action Config Types ────────────────────────────────────────────
export const projects = pgTable("projects", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  code: text("code").notNull().unique(),
  name: text("name").notNull(),
  description: text("description"),

  ownerCompanyId: text("owner_company_id").references(() => companies.id),
  operatorCompanyId: text("operator_company_id").references(() => companies.id),
  clientName: text("client_name"),
  regionId: text("region_id").references(() => regions.id),
  country: text("country"),
  industry: text("industry"),
  sector: text("sector"),

  status: text("status").notNull().default("draft"),
  phase: text("phase").default("planning"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  actualStartDate: text("actual_start_date"),
  actualEndDate: text("actual_end_date"),

  classification: text("classification").default("standard"),
  riskTier: text("risk_tier").default("medium"),
  regulatoryFramework: text("regulatory_framework"),

  inductionRequired: boolean("induction_required").notNull().default(true),
  credentialCheckRequired: boolean("credential_check_required").notNull().default(true),
  ptwRequired: boolean("ptw_required").notNull().default(false),
  biometricRequired: boolean("biometric_required").notNull().default(false),
  drugAlcoholTestRequired: boolean("drug_alcohol_test_required").notNull().default(false),
  escortRequired: boolean("escort_required").notNull().default(false),

  defaultAccessProfileId: text("default_access_profile_id"),
  defaultPolicyId: text("default_policy_id"),
  timeZone: text("time_zone").default("UTC"),
  locale: text("locale").default("en"),
  currency: text("currency").default("USD"),

  estimatedPersonDays: integer("estimated_person_days"),

  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
  tags: text("tags").array().default([]),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertProjectSchema = createInsertSchema(projects).omit({ id: true, tenantId: true });
export type InsertProject = z.infer<typeof insertProjectSchema>;
export type Project = typeof projects.$inferSelect;

// ─── CDM Notifications ────────────────────────────────────────────────
export const cdmNotifications = pgTable("cdm_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  projectId: text("project_id").notNull().references(() => projects.id),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  principalDesignerId: text("principal_designer_id"),
  principalContractorId: text("principal_contractor_id"),
  notificationDate: text("notification_date").notNull(),
  hseReference: text("hse_reference"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertCdmNotificationSchema = createInsertSchema(cdmNotifications).omit({ id: true, tenantId: true });
export type InsertCdmNotification = z.infer<typeof insertCdmNotificationSchema>;
export type CdmNotification = typeof cdmNotifications.$inferSelect;

// ─── Sites ───────────────────────────────────────────────────────────
export const sites = pgTable("sites", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  code: text("code").notNull().unique(),
  name: text("name").notNull(),
  description: text("description"),

  siteType: text("site_type").notNull().default("construction"),
  status: text("status").notNull().default("active"),

  regionId: text("region_id").references(() => regions.id),
  country: text("country"),
  city: text("city"),
  address: text("address"),
  latitude: numeric("latitude"),
  longitude: numeric("longitude"),
  geofencePolygon: jsonb("geofence_polygon").$type<{ type: string; coordinates: number[][][] }>(),

  ownerCompanyId: text("owner_company_id").references(() => companies.id),
  operatorCompanyId: text("operator_company_id").references(() => companies.id),

  accessControlEnabled: boolean("access_control_enabled").notNull().default(true),
  inductionRequired: boolean("induction_required").notNull().default(true),
  credentialCheckRequired: boolean("credential_check_required").notNull().default(true),
  ptwRequired: boolean("ptw_required").notNull().default(false),
  biometricRequired: boolean("biometric_required").notNull().default(false),
  escortRequired: boolean("escort_required").notNull().default(false),

  defaultPolicyId: text("default_policy_id"),
  defaultTemplateId: text("default_template_id"),
  linkedCompanies: text("linked_companies").array().default([]),
  emergencyAssemblyPoint: text("emergency_assembly_point"),
  maxCapacity: integer("max_capacity"),
  timeZone: text("time_zone").default("UTC"),

  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
  tags: text("tags").array().default([]),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  isDraft: boolean("is_draft").notNull().default(false),
  draftSource: text("draft_source"),
}, (t) => ({
  idxSitesTenantStatus: index("idx_sites_tenant_status").on(t.tenantId, t.status),
}));
export const insertSiteSchema = createInsertSchema(sites).omit({ id: true, tenantId: true });
export type InsertSite = z.infer<typeof insertSiteSchema>;
export type Site = typeof sites.$inferSelect;

// ─── Topology Nodes ──────────────────────────────────────────────────
export const topologyNodes = pgTable("topology_nodes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  code: text("code").notNull(),
  name: text("name").notNull(),
  description: text("description"),

  siteId: text("site_id").notNull().references(() => sites.id),
  parentNodeId: text("parent_node_id"),
  nodeType: text("node_type").notNull().default("area"),
  level: integer("level").notNull().default(0),
  depth: integer("depth").notNull().default(0),
  sortOrder: integer("sort_order").default(0),

  status: text("status").notNull().default("active"),

  latitude: numeric("latitude"),
  longitude: numeric("longitude"),
  elevation: numeric("elevation"),
  geofencePolygon: jsonb("geofence_polygon").$type<{ type: string; coordinates: number[][][] }>(),
  floorPlanRef: text("floor_plan_ref"),

  accessControlled: boolean("access_controlled").notNull().default(false),
  policyId: text("policy_id"),
  hazardous: boolean("hazardous").notNull().default(false),
  hazardTags: text("hazard_tags").array().default([]),
  requiredPPE: text("required_ppe").array().default([]),

  isTemporary: boolean("is_temporary").notNull().default(false),
  temporaryStartDate: text("temporary_start_date"),
  temporaryEndDate: text("temporary_end_date"),

  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
  tags: text("tags").array().default([]),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertTopologyNodeSchema = createInsertSchema(topologyNodes).omit({ id: true, tenantId: true });
export type InsertTopologyNode = z.infer<typeof insertTopologyNodeSchema>;
export type TopologyNode = typeof topologyNodes.$inferSelect;

// ─── Areas ───────────────────────────────────────────────────────────
export const areas = pgTable("areas", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  code: text("code").notNull(),
  name: text("name").notNull(),
  description: text("description"),

  projectId: text("project_id").references(() => projects.id),
  siteId: text("site_id").notNull().references(() => sites.id),
  topologyNodeId: text("topology_node_id").references(() => topologyNodes.id),
  parentAreaId: text("parent_area_id"),

  areaType: text("area_type").notNull().default("operational"),
  operationalClassification: text("operational_classification").default("general"),
  status: text("status").notNull().default("active"),

  hazardous: boolean("hazardous").notNull().default(false),
  hazardTags: text("hazard_tags").array().default([]),
  requiredPPE: text("required_ppe").array().default([]),
  riskLevel: text("risk_level").default("low"),

  inductionRequired: boolean("induction_required").notNull().default(false),
  credentialRequired: boolean("credential_required").notNull().default(false),
  ptwRequired: boolean("ptw_required").notNull().default(false),
  escortRequired: boolean("escort_required").notNull().default(false),
  biometricRequired: boolean("biometric_required").notNull().default(false),
  supervisorRequired: boolean("supervisor_required").notNull().default(false),
  qrDynamicRequired: boolean("qr_dynamic_required").notNull().default(false),

  maxOccupancy: integer("max_occupancy"),
  currentOccupancy: integer("current_occupancy").default(0),
  occupancyEnforced: boolean("occupancy_enforced").notNull().default(false),

  accessHoursStart: text("access_hours_start"),
  accessHoursEnd: text("access_hours_end"),
  accessDays: text("access_days").array().default([]),
  is24x7: boolean("is_24x7").notNull().default(true),

  effectiveDate: text("effective_date"),
  expiryDate: text("expiry_date"),

  emergencyAssemblyPoint: text("emergency_assembly_point"),
  lockdownStatus: text("lockdown_status").default("normal"),

  activePolicyId: text("active_policy_id"),
  allowedWorkTypes: text("allowed_work_types").array().default([]),
  assignedGates: text("assigned_gates").array().default([]),
  timeRestriction: text("time_restriction").default("24/7"),
  ptwMandatory: boolean("ptw_mandatory").notNull().default(false),
  linkedCompanies: text("linked_companies").array().default([]),

  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
  tags: text("tags").array().default([]),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  isDraft: boolean("is_draft").notNull().default(false),
  draftSource: text("draft_source"),
}, (t) => ({
  idxAreasTenantStatus: index("idx_areas_tenant_status").on(t.tenantId, t.status),
  idxAreasTenantSiteId: index("idx_areas_tenant_site_id").on(t.tenantId, t.siteId),
}));
export const insertAreaSchema = createInsertSchema(areas).omit({ id: true, tenantId: true });
export type InsertArea = z.infer<typeof insertAreaSchema>;
export type Area = typeof areas.$inferSelect;

// ─── Gates ───────────────────────────────────────────────────────────
export const gates = pgTable("gates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  code: text("code").notNull(),
  name: text("name").notNull(),
  description: text("description"),

  projectId: text("project_id").references(() => projects.id),
  siteId: text("site_id").notNull().references(() => sites.id),
  topologyNodeId: text("topology_node_id").references(() => topologyNodes.id),
  sourceAreaId: text("source_area_id").references(() => areas.id),
  targetAreaId: text("target_area_id").references(() => areas.id),

  gateType: text("gate_type").notNull().default("pedestrian"),
  mode: text("mode").notNull().default("manual"),
  direction: text("direction").notNull().default("bidirectional"),
  status: text("status").notNull().default("active"),

  operatingHoursStart: text("operating_hours_start"),
  operatingHoursEnd: text("operating_hours_end"),
  operatingDays: text("operating_days").array().default([]),
  is24x7: boolean("is_24x7").notNull().default(true),

  hardwareMetadata: jsonb("hardware_metadata").$type<Record<string, any>>().default({}),
  deviceIds: text("device_ids").array().default([]),

  emergencyBehavior: text("emergency_behavior").default("lock_open"),
  staffingModel: text("staffing_model").default("unstaffed"),

  latitude: numeric("latitude"),
  longitude: numeric("longitude"),

  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
  tags: text("tags").array().default([]),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxGatesTenantStatus: index("idx_gates_tenant_status").on(t.tenantId, t.status),
  idxGatesTenantSiteId: index("idx_gates_tenant_site_id").on(t.tenantId, t.siteId),
}));
export const insertGateSchema = createInsertSchema(gates).omit({ id: true, tenantId: true });
export type InsertGate = z.infer<typeof insertGateSchema>;
export type Gate = typeof gates.$inferSelect;

// ─── Project–Site Link ───────────────────────────────────────────────
export const projectSites = pgTable("project_sites", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  projectId: text("project_id").notNull().references(() => projects.id),
  siteId: text("site_id").notNull().references(() => sites.id),
  role: text("role").default("primary"),
  status: text("status").notNull().default("active"),
  assignedDate: text("assigned_date"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertProjectSiteSchema = createInsertSchema(projectSites).omit({ id: true, tenantId: true });
export type InsertProjectSite = z.infer<typeof insertProjectSiteSchema>;
export type ProjectSite = typeof projectSites.$inferSelect;

// ─── Project–Company Link ────────────────────────────────────────────
export const projectCompanies = pgTable("project_companies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  projectId: text("project_id").notNull().references(() => projects.id),
  companyId: text("company_id").notNull().references(() => companies.id),
  role: text("role").default("subcontractor"),
  relationshipType: text("relationship_type").notNull().default("performing"),
  status: text("status").notNull().default("active"),
  contractRef: text("contract_ref"),
  scopeOfWork: text("scope_of_work"),
  mobilizationStatus: text("mobilization_status").notNull().default("not_started"),
  onboardingStatus: text("onboarding_status").notNull().default("not_started"),
  complianceStatus: text("compliance_status").notNull().default("pending"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  notes: text("notes"),
  participationRoleTypeId: text("participation_role_type_id"),
  tenancyParticipationId: text("tenancy_participation_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertProjectCompanySchema = createInsertSchema(projectCompanies).omit({ id: true, tenantId: true });
export type InsertProjectCompany = z.infer<typeof insertProjectCompanySchema>;
export type ProjectCompany = typeof projectCompanies.$inferSelect;
export const companyProjectLinks = projectCompanies;
export const insertCompanyProjectLinkSchema = insertProjectCompanySchema;
export type InsertCompanyProjectLink = InsertProjectCompany;
export type CompanyProjectLink = ProjectCompany;

// ─── Project–Worker Link ─────────────────────────────────────────────
export const projectWorkers = pgTable("project_workers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  projectId: text("project_id").notNull().references(() => projects.id, { onDelete: 'cascade' }),
  workerId: text("worker_id").notNull().references(() => workers.id, { onDelete: 'cascade' }),
  companyId: text("company_id").references(() => companies.id),
  role: text("role"),
  status: text("status").notNull().default("active"),
  inductionStatus: text("induction_status").default("pending"),
  accessStatus: text("access_status").default("pending"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxProjectWorkersProjectTenant: index("idx_project_workers_project_tenant").on(t.projectId, t.tenantId),
}));
export const insertProjectWorkerSchema = createInsertSchema(projectWorkers).omit({ id: true, tenantId: true });
export type InsertProjectWorker = z.infer<typeof insertProjectWorkerSchema>;
export type ProjectWorker = typeof projectWorkers.$inferSelect;

// ─── Area–Company Authorization ──────────────────────────────────────
export const areaCompanies = pgTable("area_companies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  areaId: text("area_id").notNull().references(() => areas.id),
  companyId: text("company_id").notNull().references(() => companies.id),
  scopeType: text("scope_type").notNull().default("allowed"),
  relationshipPurpose: text("relationship_purpose"),
  status: text("status").notNull().default("active"),
  authorizedBy: text("authorized_by"),
  projectScope: text("project_scope"),
  siteScope: text("site_scope"),
  startDate: text("start_date"),
  endDate: text("end_date"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertAreaCompanySchema = createInsertSchema(areaCompanies).omit({ id: true, tenantId: true });
export type InsertAreaCompany = z.infer<typeof insertAreaCompanySchema>;
export type AreaCompany = typeof areaCompanies.$inferSelect;
export const companyAreaLinks = areaCompanies;
export const insertCompanyAreaLinkSchema = insertAreaCompanySchema;
export type InsertCompanyAreaLink = InsertAreaCompany;
export type CompanyAreaLink = AreaCompany;

// ─── Area Restrictions ───────────────────────────────────────────────
export const areaRestrictions = pgTable("area_restrictions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  areaId: text("area_id").notNull().references(() => areas.id),
  restrictionType: text("restriction_type").notNull(),
  reason: text("reason"),
  severity: text("severity").notNull().default("standard"),
  status: text("status").notNull().default("active"),
  startTime: text("start_time").notNull(),
  endTime: text("end_time"),
  imposedBy: text("imposed_by"),
  linkedIncidentId: text("linked_incident_id"),
  affectsAccess: boolean("affects_access").notNull().default(true),
  affectsPtw: boolean("affects_ptw").notNull().default(true),
  scope: text("scope").notNull().default("all_workers"),
  scopeTargetId: text("scope_target_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

const AREA_RESTRICTION_SCOPES = ["all_workers", "company", "role"] as const;
export type AreaRestrictionScope = typeof AREA_RESTRICTION_SCOPES[number];
export const insertAreaRestrictionSchema = createInsertSchema(areaRestrictions)
  .omit({ id: true, tenantId: true })
  .extend({
    scope: z.enum(AREA_RESTRICTION_SCOPES).default("all_workers"),
  });
export type InsertAreaRestriction = z.infer<typeof insertAreaRestrictionSchema>;
export type AreaRestriction = typeof areaRestrictions.$inferSelect;

// ─── Workforce Readiness: Inductions ─────────────────────────────────
export const navigationConfig = pgTable("navigation_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  role: text("role").notNull(),
  visibility: jsonb("visibility").$type<Record<string, boolean>>().notNull().default({}),
  landingPage: text("landing_page"),
});
export const insertNavigationConfigSchema = createInsertSchema(navigationConfig).omit({ id: true, tenantId: true });
export type InsertNavigationConfig = z.infer<typeof insertNavigationConfigSchema>;
export type NavigationConfig = typeof navigationConfig.$inferSelect;

// ─── Notification Audit Events ───────────────────────────────────────
export const visitors = pgTable("visitors", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  company: text("company"),
  email: text("email"),
  phone: text("phone"),
  idType: text("id_type"),
  idNumber: text("id_number"),
  hostName: text("host_name"),
  hostDepartment: text("host_department"),
  purpose: text("purpose"),
  siteId: text("site_id").references(() => sites.id),
  areaId: text("area_id").references(() => areas.id),
  badgeNumber: text("badge_number"),
  checkInTime: text("check_in_time"),
  checkOutTime: text("check_out_time"),
  expectedDuration: text("expected_duration"),
  status: text("status").notNull().default("pre-registered"),
  inductionCompleted: boolean("induction_completed").notNull().default(false),
  ndaSigned: boolean("nda_signed").notNull().default(false),
  photoUrl: text("photo_url"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertVisitorSchema = createInsertSchema(visitors).omit({ id: true, tenantId: true });
export type InsertVisitor = z.infer<typeof insertVisitorSchema>;
export type Visitor = typeof visitors.$inferSelect;

// ─── Devices ─────────────────────────────────────────────────────────
export const devices = pgTable("devices", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  deviceType: text("device_type").notNull().default("reader"),
  model: text("model"),
  manufacturer: text("manufacturer"),
  serialNumber: text("serial_number"),
  firmwareVersion: text("firmware_version"),
  ipAddress: text("ip_address"),
  macAddress: text("mac_address"),
  siteId: text("site_id").references(() => sites.id),
  areaId: text("area_id").references(() => areas.id),
  gateId: text("gate_id").references(() => gates.id),
  location: text("location"),
  status: text("status").notNull().default("online"),
  lastHeartbeat: text("last_heartbeat"),
  lastMaintenanceDate: text("last_maintenance_date"),
  nextMaintenanceDate: text("next_maintenance_date"),
  installDate: text("install_date"),
  warrantyExpiry: text("warranty_expiry"),
  configuration: jsonb("configuration").$type<Record<string, any>>().default({}),
  tags: text("tags").array().default([]),
  supportedQrModes: jsonb("supported_qr_modes").$type<string[]>().default(["jwt_dynamic", "printed_stable"]),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertDeviceSchema = createInsertSchema(devices).omit({ id: true, tenantId: true });
export type InsertDevice = z.infer<typeof insertDeviceSchema>;
export type Device = typeof devices.$inferSelect;

// ─── Regions (Super Admin) ───────────────────────────────────────────
export const regions = pgTable("regions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  country: text("country"),
  timeZone: text("time_zone"),
  currency: text("currency"),
  siteCount: integer("site_count").default(0),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertRegionSchema = createInsertSchema(regions).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertRegion = z.infer<typeof insertRegionSchema>;
export type Region = typeof regions.$inferSelect;
export const platformSettings = pgTable("platform_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(),
  value: text("value"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});

// ─── User Notification Preferences ───────────────────────────────────
export const userNotificationPreferences = pgTable("user_notification_preferences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: text("user_id").notNull().references(() => users.id),
  notificationId: text("notification_id").notNull(),
  enabled: boolean("enabled").notNull().default(true),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export type UserNotificationPreference = typeof userNotificationPreferences.$inferSelect;

// ─── User Sessions ────────────────────────────────────────────────────
export const userSessions = pgTable("user_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: text("user_id").notNull().references(() => users.id),
  sessionToken: text("session_token").notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  lastActiveAt: timestamp("last_active_at", { withTimezone: true }).defaultNow(),
  isActive: boolean("is_active").notNull().default(true),
}, (t) => ({
  idxUserSessionsSessionToken: index("idx_user_sessions_session_token").on(t.sessionToken),
  // Pack 5 (Task #924): backport of legacy migration 0075_sa_perf_indexes idx_user_sessions_user_active
  idxUserSessionsUserActive: index("idx_user_sessions_user_active").on(t.userId, t.isActive),
}));
export const insertUserSessionSchema = createInsertSchema(userSessions).omit({ id: true, createdAt: true, lastActiveAt: true });
export type InsertUserSession = z.infer<typeof insertUserSessionSchema>;
export type UserSession = typeof userSessions.$inferSelect;
export const insertPlatformSettingSchema = createInsertSchema(platformSettings).omit({ id: true, updatedAt: true });
export type InsertPlatformSetting = z.infer<typeof insertPlatformSettingSchema>;
export type PlatformSetting = typeof platformSettings.$inferSelect;
export const supportTickets = pgTable("support_tickets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").references(() => tenants.id),
  submitterUserId: text("submitter_user_id").references(() => users.id),
  issueType: text("issue_type").notNull(),
  priority: text("priority").notNull().default("medium"),
  subject: text("subject").notNull(),
  description: text("description").notNull(),
  status: text("status").notNull().default("open"),
  // Entity linking
  linkedEntityType: text("linked_entity_type"),
  linkedEntityId: text("linked_entity_id"),
  linkedEntityName: text("linked_entity_name"),
  internalNotes: text("internal_notes"),
  // SLA tracking
  slaStatus: text("sla_status"),
  slaTargetResponseAt: timestamp("sla_target_response_at", { withTimezone: true }),
  slaTargetResolutionAt: timestamp("sla_target_resolution_at", { withTimezone: true }),
  firstResponseAt: timestamp("first_response_at", { withTimezone: true }),
  resolvedAt: timestamp("resolved_at", { withTimezone: true }),
  // Resolution context
  resolutionCategory: text("resolution_category"),
  resolutionSummary: text("resolution_summary"),
  resolutionAuditLogRef: text("resolution_audit_log_ref"),
  followUpRecommendations: text("follow_up_recommendations"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertSupportTicketSchema = createInsertSchema(supportTickets).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertSupportTicket = z.infer<typeof insertSupportTicketSchema>;
export type SupportTicket = typeof supportTickets.$inferSelect;
export const supportTicketAttachments = pgTable("support_ticket_attachments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ticketId: text("ticket_id").notNull().references(() => supportTickets.id),
  filename: text("filename").notNull(),
  filePath: text("file_path").notNull(),
  mimeType: text("mime_type").notNull(),
  size: integer("size").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertSupportTicketAttachmentSchema = createInsertSchema(supportTicketAttachments).omit({ id: true, createdAt: true });
export type InsertSupportTicketAttachment = z.infer<typeof insertSupportTicketAttachmentSchema>;
export type SupportTicketAttachment = typeof supportTicketAttachments.$inferSelect;
export const supportTicketReplies = pgTable("support_ticket_replies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ticketId: text("ticket_id").notNull().references(() => supportTickets.id),
  authorUserId: text("author_user_id").references(() => users.id),
  message: text("message").notNull(),
  authorRole: text("author_role"),
  isInternal: boolean("is_internal").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertSupportTicketReplySchema = createInsertSchema(supportTicketReplies).omit({ id: true, createdAt: true });
export type InsertSupportTicketReply = z.infer<typeof insertSupportTicketReplySchema>;
export type SupportTicketReply = typeof supportTicketReplies.$inferSelect;

// ─── Worker Onboarding: Self-Service Invitations ─────────────────────
export const companyTenancyParticipations = pgTable("company_tenancy_participations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: text("company_id").notNull().references(() => companies.id),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  participationRoleTypeId: text("participation_role_type_id").notNull().references(() => participationRoleTypes.id),
  status: text("status").notNull().default("active"),
  effectiveFrom: timestamp("effective_from", { withTimezone: true }),
  effectiveTo: timestamp("effective_to", { withTimezone: true }),
  notes: text("notes"),
  capabilityOverrides: jsonb("capability_overrides").$type<Record<string, boolean>>(),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqCompanyTenant: unique("company_tenancy_participations_company_id_tenant_id_key").on(t.companyId, t.tenantId),
  idxCtpCompanyTenant: index("idx_ctp_company_tenant").on(t.companyId, t.tenantId),
  idxCtpTenantStatus: index("idx_ctp_tenant_status").on(t.tenantId, t.status),
}));
export const insertCompanyTenancyParticipationSchema = createInsertSchema(companyTenancyParticipations).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertCompanyTenancyParticipation = z.infer<typeof insertCompanyTenancyParticipationSchema>;
export type CompanyTenancyParticipation = typeof companyTenancyParticipations.$inferSelect;

// ─── Company–Project Participations ──────────────────────────────────
export const companyProjectParticipations = pgTable("company_project_participations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: text("company_id").notNull().references(() => companies.id),
  projectId: text("project_id").notNull().references(() => projects.id),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  participationRoleTypeId: text("participation_role_type_id").references(() => participationRoleTypes.id),
  canDeployWorkers: boolean("can_deploy_workers").notNull().default(false),
  canCreatePtw: boolean("can_create_ptw").notNull().default(false),
  canApprovePtw: boolean("can_approve_ptw").notNull().default(false),
  accessTier: text("access_tier").notNull().default("worker"),
  ptwRolesGranted: text("ptw_roles_granted").array().default([]),
  deniedPermissions: text("denied_permissions").array().default([]),
  // Project-level capability overrides (Task #219)
  canOwnInvestigation: boolean("can_own_investigation"),
  canIssueCredentials: boolean("can_issue_credentials"),
  isAuditOnly: boolean("is_audit_only").notNull().default(false),
  maxWorkerCount: integer("max_worker_count"),
  piiMaskingRequired: boolean("pii_masking_required").notNull().default(false),
  allowedPtwCategories: text("allowed_ptw_categories").array().default([]),
  autoSuspendOnComplianceExpiry: boolean("auto_suspend_on_compliance_expiry").notNull().default(false),
  status: text("status").notNull().default("active"),
  effectiveFrom: timestamp("effective_from", { withTimezone: true }),
  effectiveTo: timestamp("effective_to", { withTimezone: true }),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqCompanyProjectTenant: unique("company_project_participations_company_id_project_id_tenant_id_").on(t.companyId, t.projectId, t.tenantId),
}));
export const insertCompanyProjectParticipationSchema = createInsertSchema(companyProjectParticipations).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertCompanyProjectParticipation = z.infer<typeof insertCompanyProjectParticipationSchema>;
export type CompanyProjectParticipation = typeof companyProjectParticipations.$inferSelect;

// ─── Departments ──────────────────────────────────────────────────────
export const departments = pgTable("departments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: text("company_id").notNull().references(() => companies.id),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  parentDeptId: text("parent_dept_id"),
  name: text("name").notNull(),
  code: text("code"),
  budgetCode: text("budget_code"),
  description: text("description"),
  headWorkerId: text("head_worker_id").references(() => workers.id),
  defaultPtwRoles: text("default_ptw_roles").array().default([]),
  requiredCredentialTypes: text("required_credential_types").array().default([]),
  credentialTemplateIds: text("credential_template_ids").array().default([]),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertDepartmentSchema = createInsertSchema(departments).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDepartment = z.infer<typeof insertDepartmentSchema>;
export type Department = typeof departments.$inferSelect;

// ─── Department Memberships ───────────────────────────────────────────
export const departmentMemberships = pgTable("department_memberships", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  departmentId: text("department_id").notNull().references(() => departments.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  role: text("role").default("member"),
  joinedAt: timestamp("joined_at", { withTimezone: true }).defaultNow(),
  leftAt: timestamp("left_at", { withTimezone: true }),
  status: text("status").notNull().default("active"),
});
export const insertDepartmentMembershipSchema = createInsertSchema(departmentMemberships).omit({ id: true, joinedAt: true });
export type InsertDepartmentMembership = z.infer<typeof insertDepartmentMembershipSchema>;
export type DepartmentMembership = typeof departmentMemberships.$inferSelect;

// ─── Divisions ────────────────────────────────────────────────────────
export const divisions = pgTable("divisions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  departmentId: text("department_id").notNull().references(() => departments.id),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  code: text("code"),
  description: text("description"),
  leadWorkerId: text("lead_worker_id").references(() => workers.id),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertDivisionSchema = createInsertSchema(divisions).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDivision = z.infer<typeof insertDivisionSchema>;
export type Division = typeof divisions.$inferSelect;

// ─── Division Memberships ─────────────────────────────────────────────
export const divisionMemberships = pgTable("division_memberships", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  divisionId: text("division_id").notNull().references(() => divisions.id),
  workerId: text("worker_id").notNull().references(() => workers.id),
  role: text("role").default("member"),
  joinedAt: timestamp("joined_at", { withTimezone: true }).defaultNow(),
  leftAt: timestamp("left_at", { withTimezone: true }),
  status: text("status").notNull().default("active"),
});
export const insertDivisionMembershipSchema = createInsertSchema(divisionMemberships).omit({ id: true, joinedAt: true });
export type InsertDivisionMembership = z.infer<typeof insertDivisionMembershipSchema>;
export type DivisionMembership = typeof divisionMemberships.$inferSelect;

// ─── Division Project Deployments ────────────────────────────────────
export const divisionProjectDeployments = pgTable("division_project_deployments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  divisionId: text("division_id").references(() => divisions.id),
  projectId: text("project_id").notNull().references(() => projects.id),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  status: text("status").notNull().default("active"),
  deployedAt: timestamp("deployed_at", { withTimezone: true }),
  redeployedAt: timestamp("redeployed_at", { withTimezone: true }),
  notes: text("notes"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertDivisionProjectDeploymentSchema = createInsertSchema(divisionProjectDeployments).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDivisionProjectDeployment = z.infer<typeof insertDivisionProjectDeploymentSchema>;
export type DivisionProjectDeployment = typeof divisionProjectDeployments.$inferSelect;

// ─── User Project Role Assignments ───────────────────────────────────
export const userProjectRoleAssignments = pgTable("user_project_role_assignments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: text("user_id").notNull().references(() => users.id),
  projectId: text("project_id").notNull().references(() => projects.id),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  companyId: text("company_id").references(() => companies.id),
  roleName: text("role_name"),
  permissions: text("permissions").array().default([]),
  ptwRoles: text("ptw_roles").array().default([]),
  status: text("status").notNull().default("active"),
  effectiveFrom: timestamp("effective_from", { withTimezone: true }),
  effectiveTo: timestamp("effective_to", { withTimezone: true }),
  overrideAuditOnly: boolean("override_audit_only").default(false),
  assignedBy: text("assigned_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqUserProject: unique("user_project_role_assignments_user_id_project_id_key").on(t.userId, t.projectId),
}));
export const insertUserProjectRoleAssignmentSchema = createInsertSchema(userProjectRoleAssignments).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertUserProjectRoleAssignment = z.infer<typeof insertUserProjectRoleAssignmentSchema>;
export type UserProjectRoleAssignment = typeof userProjectRoleAssignments.$inferSelect;

// ─── Authorization Audit Log ──────────────────────────────────────────
export const authorizationAuditLog = pgTable("authorization_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").references(() => tenants.id),
  userId: text("user_id").references(() => users.id),
  permission: text("permission").notNull(),
  resource: text("resource"),
  decision: text("decision").notNull().default("denied"),
  resolvedRole: text("resolved_role"),
  projectId: text("project_id"),
  companyId: text("company_id"),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  impersonatedBy: text("impersonated_by"),
  signature: text("signature"),
  eventType: text("event_type"),
  targetEntityId: text("target_entity_id"),
  targetEntityType: text("target_entity_type"),
  previousValue: jsonb("previous_value"),
  newValue: jsonb("new_value"),
  // Task #1961 (Pack 10.F closure) — tamper-evident hash chain.
  // See `server/engines/audit-chain.ts` and migration 0136. Columns are
  // NULLABLE so legacy direct inserts continue to work; the verifier
  // reports unsigned rows as `partial_chain` rather than `tampered`.
  prevHash: text("prev_hash"),
  rowHash: text("row_hash"),
  chainPosition: bigint("chain_position", { mode: "number" }),
  timestamp: timestamp("timestamp", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxAuthAuditLogTenantTimestamp: index("idx_auth_audit_log_tenant_timestamp").on(t.tenantId, t.timestamp),
  idxAuthAuditLogTenantUserId: index("idx_auth_audit_log_tenant_user_id").on(t.tenantId, t.userId),
  // Pack 5 (Task #924): backport of legacy migration 0075_sa_perf_indexes
  idxAuthzAuditActorTimestamp: index("idx_authz_audit_actor_timestamp").on(t.userId, t.timestamp),
  idxAuthzAuditResourceTimestamp: index("idx_authz_audit_resource_timestamp").on(t.targetEntityType, t.targetEntityId, t.timestamp),
  idxAuthzAuditChain: index("authorization_audit_log_chain_idx").on(t.tenantId, t.chainPosition),
}));
export const insertAuthorizationAuditLogSchema = createInsertSchema(authorizationAuditLog).omit({ id: true, timestamp: true });
export type InsertAuthorizationAuditLog = z.infer<typeof insertAuthorizationAuditLogSchema>;
export type AuthorizationAuditLogEntry = typeof authorizationAuditLog.$inferSelect;

// ─── Toolbox Talks / Pre-Start Briefings ─────────────────────────────
export const tenantFeatures = pgTable("tenant_features", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  featureKey: text("feature_key").notNull(),
  enabled: boolean("enabled").notNull().default(false),
  config: jsonb("config").$type<Record<string, any>>().default({}),
  previewExpiresAt: timestamp("preview_expires_at", { withTimezone: true }),
  enabledAt: timestamp("enabled_at", { withTimezone: true }),
  enabledBy: text("enabled_by"),
}, (t) => ({
  uniqTenantFeature: unique("tenant_features_tenant_feature").on(t.tenantId, t.featureKey),
}));
export const insertTenantFeatureSchema = createInsertSchema(tenantFeatures).omit({ id: true });
export type InsertTenantFeature = z.infer<typeof insertTenantFeatureSchema>;
export type TenantFeature = typeof tenantFeatures.$inferSelect;

// ─── Configuration Packs ──────────────────────────────────────────────
// ─── Company Type Registry ─────────────────────────────────────────────────
export const passwordResetTokens = pgTable("password_reset_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: text("user_id").notNull().references(() => users.id),
  token: text("token").notNull().unique(),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  usedAt: timestamp("used_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertPasswordResetTokenSchema = createInsertSchema(passwordResetTokens).omit({ id: true, createdAt: true });
export type InsertPasswordResetToken = z.infer<typeof insertPasswordResetTokenSchema>;
export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;

// ─── Email Audit Log ─────────────────────────────────────────────────────────
export const emailAuditLog = pgTable("email_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  recipient: text("recipient").notNull(),
  subject: text("subject").notNull(),
  emailType: text("email_type").notNull(),
  status: text("status").notNull().default("sent"),
  messageId: text("message_id"),
  errorMessage: text("error_message"),
  relatedEntityType: text("related_entity_type"),
  relatedEntityId: text("related_entity_id"),
  tenantId: text("tenant_id"),
  actorId: text("actor_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertEmailAuditLogSchema = createInsertSchema(emailAuditLog).omit({ id: true, createdAt: true });
export type InsertEmailAuditLog = z.infer<typeof insertEmailAuditLogSchema>;
export type EmailAuditLogEntry = typeof emailAuditLog.$inferSelect;

// ─── Notification Suppressions ───────────────────────────────────────────────
// Pack 10.E (Task #1025) — Cross-channel suppression registry.
//
// Canonical registry of recipients who must NOT receive further notifications
// on a given channel. Populated by:
//   • Email provider webhooks on hard bounce / complaint
//     (`server/routes/webhooks/email.ts`).
//   • SMS opt-out webhook on `STOP` / vendor opt-out callback
//     (`server/routes/webhooks/sms.ts`).
//   • Manual administrative suppression (super-admin tooling — future).
//
// The (channel, recipient) pair is unique. `recipient` is normalised
// (lowercased email; E.164 phone). `recipient_hash` is the SHA-256 of the
// normalised recipient — useful for audit-trail joins where we never want
// the plaintext recipient surfaced.
export const NOTIFICATION_SUPPRESSION_CHANNELS = ["email", "sms"] as const;
export type NotificationSuppressionChannel = typeof NOTIFICATION_SUPPRESSION_CHANNELS[number];

export const NOTIFICATION_SUPPRESSION_SOURCES = [
  "hard_bounce",
  "soft_bounce",
  "complaint",
  "unsubscribe",
  "manual",
  "policy",
] as const;
export type NotificationSuppressionSource = typeof NOTIFICATION_SUPPRESSION_SOURCES[number];

export const notificationSuppressions = pgTable(
  "notification_suppressions",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    channel: text("channel").notNull(),
    recipient: text("recipient").notNull(),
    recipientHash: text("recipient_hash").notNull(),
    reason: text("reason").notNull(),
    source: text("source").notNull(),
    provider: text("provider"),
    providerEventId: text("provider_event_id"),
    tenantId: text("tenant_id"),
    context: jsonb("context").$type<Record<string, unknown>>().default({}),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (t) => ({
    uqChannelRecipient: uniqueIndex("notification_suppressions_channel_recipient_uq").on(
      t.channel,
      t.recipient,
    ),
    idxRecipientHash: index("notification_suppressions_recipient_hash_idx").on(t.recipientHash),
  }),
);
export const insertNotificationSuppressionSchema = createInsertSchema(notificationSuppressions).omit({
  id: true,
  createdAt: true,
});
export type InsertNotificationSuppression = z.infer<typeof insertNotificationSuppressionSchema>;
export type NotificationSuppression = typeof notificationSuppressions.$inferSelect;

// ─── Partner Portal Schema ────────────────────────────────────────────────────

// Partner Access Profiles: default capability baselines keyed by company_type
export const companyInvitations = pgTable("company_invitations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: text("company_id").notNull().references(() => companies.id),
  email: text("email").notNull(),
  role: text("role").notNull().default("member"),
  token: text("token").notNull().unique(),
  status: text("status").notNull().default("pending"),
  invitedBy: text("invited_by").references(() => users.id),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  acceptedAt: timestamp("accepted_at", { withTimezone: true }),
  acceptedByUserId: text("accepted_by_user_id").references(() => users.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertCompanyInvitationSchema = createInsertSchema(companyInvitations).omit({ id: true, createdAt: true, acceptedAt: true });
export type InsertCompanyInvitation = z.infer<typeof insertCompanyInvitationSchema>;
export type CompanyInvitation = typeof companyInvitations.$inferSelect;


// Company Evidence Documents: partner-uploaded evidence files
export const companyEvidenceDocuments = pgTable("company_evidence_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: text("company_id").notNull().references(() => companies.id),
  title: text("title").notNull(),
  documentType: text("document_type").notNull(),
  fileUrl: text("file_url").notNull(),
  fileKey: text("file_key"),
  fileSizeBytes: integer("file_size_bytes"),
  mimeType: text("mime_type"),
  status: text("status").notNull().default("pending"),
  rejectionReason: text("rejection_reason"),
  verifiedBy: text("verified_by").references(() => users.id),
  verifiedAt: timestamp("verified_at", { withTimezone: true }),
  uploadedBy: text("uploaded_by").references(() => users.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertCompanyEvidenceDocumentSchema = createInsertSchema(companyEvidenceDocuments).omit({ id: true, createdAt: true, updatedAt: true, verifiedAt: true });
export type InsertCompanyEvidenceDocument = z.infer<typeof insertCompanyEvidenceDocumentSchema>;
export type CompanyEvidenceDocument = typeof companyEvidenceDocuments.$inferSelect;

// Note: the legacy `sms_audit_log` table was retired by Task #936 once the
// canonical `audit_events` rows for outbound SMS (event_key prefix `sms.`)
// had ~30 days of confidence in production. The table and its writer
// `createSmsAuditLog` were removed; consumers now query `audit_events`
// filtered by `event_key IN ('sms.send_attempted','sms.send_succeeded',
// 'sms.send_failed')`. Migration `0083_drop_sms_audit_log.sql` drops the
// table after the final cold-storage export.

// ── Personal Access Tokens (PAT) ────────────────────────────────────────────
// Enables Bearer-token authentication for mobile/SDK clients alongside
// the existing session-cookie auth used by the web app.
export const personalAccessTokens = pgTable("personal_access_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  tokenHash: text("token_hash").notNull().unique(),
  lastUsedAt: timestamp("last_used_at", { withTimezone: true }),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
  scopes: text("scopes").array().default([]),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertPersonalAccessTokenSchema = createInsertSchema(personalAccessTokens).omit({
  id: true,
  tokenHash: true,
  lastUsedAt: true,
  createdAt: true,
});
export type InsertPersonalAccessToken = z.infer<typeof insertPersonalAccessTokenSchema>;
export type PersonalAccessToken = typeof personalAccessTokens.$inferSelect;

// ── PAT Usage Audit Log ──────────────────────────────────────────────────────
// Records every request authenticated via a Personal Access Token for
// forensic/compliance purposes (RR-045). Writes are fire-and-forget.
export const patUsageAuditLog = pgTable("pat_usage_audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tokenId: text("token_id").notNull().references(() => personalAccessTokens.id, { onDelete: "cascade" }),
  tenantId: text("tenant_id"),
  endpoint: text("endpoint").notNull(),
  httpMethod: text("http_method").notNull(),
  responseCode: integer("response_code").notNull(),
  ipAddress: text("ip_address"),
  timestamp: timestamp("timestamp", { withTimezone: true }).defaultNow(),
}, (table) => ({
  tokenIdIdx: index("idx_pat_usage_audit_log_token_id").on(table.tokenId),
  tenantIdIdx: index("idx_pat_usage_audit_log_tenant_id").on(table.tenantId),
  timestampIdx: index("idx_pat_usage_audit_log_timestamp").on(table.timestamp),
}));
export const insertPatUsageAuditLogSchema = createInsertSchema(patUsageAuditLog).omit({ id: true, timestamp: true });
export type InsertPatUsageAuditLog = z.infer<typeof insertPatUsageAuditLogSchema>;
export type PatUsageAuditLog = typeof patUsageAuditLog.$inferSelect;

// ─── Tenant Business Configuration ──────────────────────────────────────────
// Stores configurable operational parameters for each tenant, organized by domain.
// Each domain's rules are stored as JSONB with validated defaults matching
// current hardcoded behavior.

// ─── Scheduler Locks (advisory, in-process deduplication for critical jobs) ───
// Prevents duplicate job execution when multiple server instances run concurrently.
// Each job acquires a named lock row before running and releases it after completion.
// Rows with expiresAt in the past are treated as released automatically.
